import {
  useCreateLocationAlarmIncident,
  useLocationById,
} from '@hakimo-ui/hakimo/data-access';
import {
  Situation,
  UnifiedMonitoringProduct,
  UpsertLocationAlarmIncidentDTO,
  UpsertLocationAlarmIncidentResponseDTO,
} from '@hakimo-ui/hakimo/types';
import {
  isExistingIncidentResponse,
  toast,
  useUser,
} from '@hakimo-ui/hakimo/util';
import { useEffect, useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { withSubSituation } from './util';

interface Options {
  locationId: string;
  locationAlarmId: string;
  navToEscalation: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  selectedSituation?: Situation;
  product?: UnifiedMonitoringProduct;
}

export function usePreEscalate(options: Options) {
  const {
    locationId,
    locationAlarmId,
    navToEscalation,
    selectedSituation,
    product,
  } = options;

  const isScanContext = product === UnifiedMonitoringProduct.SCAN;
  const [subSituation, setSubSituation] = useState<string>();
  const [isTextEdited, setIsTextEdited] = useState(false);
  const [textValue, setTextValue] = useState(
    selectedSituation ? `${selectedSituation.label}:` : ''
  );
  const [showEmptyError, setShowEmptyError] = useState(false);

  const seededText = selectedSituation
    ? `${selectedSituation.label}${subSituation ? ` - ${subSituation}` : ''}:`
    : '';

  useEffect(() => {
    if (!isTextEdited) {
      setTextValue(seededText);
    }
  }, [seededText, isTextEdited]);

  const locationDetails = useLocationById(locationId).data;
  const user = useUser();
  const { dataVersion, sourceGroupId } = useAlarmGroupStatic();

  const onSuccess = (data: UpsertLocationAlarmIncidentResponseDTO) => {
    if (isExistingIncidentResponse(data)) {
      toast(
        `This alarm is already covered by an open escalation at ${locationDetails?.name}. Resolve or update that escalation instead.`,
        { type: 'warning' }
      );
      return;
    }
    toast(`Alarm is being escalated at ${locationDetails?.name}`, {
      type: 'success',
    });
    navToEscalation(textValue, selectedSituation?.label, subSituation);
  };

  const onError = (err: Error) => {
    toast(`Could not escalate: ${err.message}`, { type: 'error' });
  };

  const mutation = useCreateLocationAlarmIncident(
    onSuccess,
    onError,
    dataVersion
  );

  const onTextValueChange = (val: string) => {
    setIsTextEdited(true);
    setTextValue(val);
    if (showEmptyError && val.trim() !== '') {
      setShowEmptyError(false);
    }
  };

  const createPayload = (comment: string): UpsertLocationAlarmIncidentDTO => ({
    location_id: +locationId,
    location_alarm_id: locationAlarmId,
    tenant_id: locationDetails?.tenant_id,
    title: `Escalation alert: Ongoing incident at ${locationDetails?.name}`,
    created_by: user.id,
    description: comment,
    source_group_id: sourceGroupId,
    product,
  });

  const handleSubSituationClick = (clicked: string) => {
    const comment = `${selectedSituation?.label} - ${clicked}`;

    if (!isScanContext) {
      mutation.mutate(createPayload(comment));
    } else {
      navToEscalation(comment, selectedSituation?.label, clicked);
    }
  };

  const onEscalate = () => {
    if (textValue.trim() === '') {
      setShowEmptyError(true);
      return;
    }
    if (!isScanContext) {
      mutation.mutate(createPayload(withSubSituation(textValue, subSituation)));
    } else {
      navToEscalation(textValue, selectedSituation?.label, subSituation);
    }
  };

  const hasSubSituations =
    !!selectedSituation?.subSituations &&
    selectedSituation.subSituations.length > 0;

  return {
    textValue,
    onTextValueChange,
    showEmptyError,
    onEscalate,
    handleSubSituationClick,
    hasSubSituations,
    subSituations: selectedSituation?.subSituations ?? [],
    subSituation,
    setSubSituation,
    isLoading: mutation.isLoading,
  };
}

export default usePreEscalate;
